/**
 * Network status icon + slow-link detection (Dashboard / Advanced Reporting).
 * All values are build-time via Vite `VITE_*` env vars.
 */

const parseBoolean = (value, defaultValue) => {
  if (value === undefined || value === "") {
    return defaultValue
  }
  const normalized = String(value).trim().toLowerCase()
  if (["true", "1", "yes", "on"].includes(normalized)) {
    return true
  }
  if (["false", "0", "no", "off"].includes(normalized)) {
    return false
  }
  return defaultValue
}

const parsePositiveNumber = (value, defaultValue) => {
  if (value === undefined || value === "") {
    return defaultValue
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return defaultValue
  }
  return parsed
}

const parseEffectiveTypes = (value, defaultValue) => {
  if (value === undefined || value === "") {
    return defaultValue
  }
  const types = String(value)
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
  return types.length > 0 ? types : defaultValue
}

const parseProbePath = (value, defaultValue) => {
  if (value === undefined || value === "") {
    return defaultValue
  }
  const path = String(value).trim()
  if (!path.startsWith("/")) {
    return defaultValue
  }
  return path
}

const DEFAULT_SLOW_EFFECTIVE_TYPES = Object.freeze(["slow-2g", "2g"])
const DEFAULT_PROBE_SLOW_MS = 8000

const networkConnectivityConfig = Object.freeze({
  /** Master switch: network status UI, offline listeners, and slow detection. */
  speedStatusEnabled: parseBoolean(
    import.meta.env.VITE_NETWORK_SPEED_STATUS_ENABLED,
    true
  ),
  /** When false, only offline messaging runs (no Network Information API / probe). */
  slowDetectionEnabled: parseBoolean(
    import.meta.env.VITE_NETWORK_SLOW_DETECTION_ENABLED,
    true
  ),
  probeEnabled: parseBoolean(
    import.meta.env.VITE_NETWORK_LATENCY_PROBE_ENABLED,
    true
  ),
  slowRttMs: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_SLOW_RTT_MS,
    2000
  ),
  slowDownlinkMbpsMax: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_SLOW_DOWNLINK_MBPS_MAX,
    0.5
  ),
  slowEffectiveTypes: parseEffectiveTypes(
    import.meta.env.VITE_NETWORK_SLOW_EFFECTIVE_TYPES,
    DEFAULT_SLOW_EFFECTIVE_TYPES
  ),
  probeUrl: parseProbePath(
    import.meta.env.VITE_NETWORK_PROBE_URL,
    "/svg/circle.svg"
  ),
  probeSlowMs: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_PROBE_SLOW_MS,
    DEFAULT_PROBE_SLOW_MS
  ),
  probeTimeoutMs: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_PROBE_TIMEOUT_MS,
    Math.max(DEFAULT_PROBE_SLOW_MS * 2, 15000)
  ),
  probeIntervalMs: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_PROBE_INTERVAL_MS,
    45000
  ),
  probeSlowStreakRequired: parsePositiveNumber(
    import.meta.env.VITE_NETWORK_PROBE_SLOW_STREAK,
    1
  )
})

export default networkConnectivityConfig
